import React, { useState } from 'react';
import { Button, Stack, Chip, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, CircularProgress } from '@mui/material';
import axios from 'axios';
import SettingsIcon from '@mui/icons-material/Settings';

export const API_BASE = '/api/v1/plans';

const PlanOptimizationCell: React.FC<{ plan: any }> = ({ plan }) => {
  const [open, setOpen] = useState(false);
  const [showRequest, setShowRequest] = useState(false);
  const [requestJson, setRequestJson] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [resultJson, setResultJson] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const meta = plan.calendar_planning_meta || {};

  // Helper to fetch and show request JSON
  const handleShowRequest = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE}/${plan.id}/calendar-planning/request-json`);
      // If backend returns a file, res.data may be an object, so stringify it
      setRequestJson(typeof res.data === 'string' ? res.data : JSON.stringify(res.data, null, 2));
      setShowRequest(true);
    } catch (e: any) {
      setRequestJson('Failed to load request JSON');
      setError(e?.response?.data?.detail || 'Failed to load request JSON');
      setShowRequest(true);
    }
    setLoading(false);
  };

  // Helper to fetch and show result JSON
  const handleShowResult = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE}/${plan.id}/calendar-planning/result`);
      setResultJson(typeof res.data === 'string' ? res.data : JSON.stringify(res.data, null, 2));
      setShowResult(true);
    } catch (e: any) {
      setResultJson('Failed to load result');
      setError(e?.response?.data?.detail || 'Failed to load result');
      setShowResult(true);
    }
    setLoading(false);
  };

  // Button handlers
  const handleGenerateRequest = async () => {
    setLoading(true);
    setError(null);
    try {
      await axios.post(`${API_BASE}/${plan.id}/calendar-planning/generate-request`);
      window.location.reload();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to generate request');
    }
    setLoading(false);
  };

  const handleRegenerateRequest = handleGenerateRequest;

  const handleStartOptimization = async () => {
    setLoading(true);
    setError(null);
    try {
      await axios.post(`${API_BASE}/${plan.id}/calendar-planning/start`);
      window.location.reload();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to start optimization');
    }
    setLoading(false);
  };

  const handlePollStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      await axios.post(`${API_BASE}/${plan.id}/calendar-planning/poll`);
      window.location.reload();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to poll status');
    }
    setLoading(false);
  };

  // Download handlers
  const handleDownloadRequest = () => {
    window.open(`${API_BASE}/${plan.id}/calendar-planning/download-request`, '_blank');
  };

  const handleDownloadResult = () => {
    window.open(`${API_BASE}/${plan.id}/calendar-planning/download-result`, '_blank');
  };

  // UI logic for modal content
  let modalContent: React.ReactNode = null;
  if (!meta.calendar_request_path) {
    // No request generated
    modalContent = (
      <Button variant="contained" size="small" onClick={handleGenerateRequest} disabled={loading} fullWidth>
        {loading ? <CircularProgress size={20} /> : 'Generate Request'}
      </Button>
    );
  } else if (!meta.calendar_job_id) {
    // Request generated, not started
    modalContent = (
      <Stack spacing={2}>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" size="small" onClick={handleShowRequest} disabled={loading}>
            Show Request
          </Button>
          <Button variant="outlined" size="small" onClick={handleDownloadRequest} disabled={loading}>
            Download Request
          </Button>
          <Button variant="outlined" size="small" onClick={handleRegenerateRequest} disabled={loading}>
            Regenerate Request
          </Button>
          <Button variant="contained" size="small" onClick={handleStartOptimization} disabled={loading}>
            Start Optimization
          </Button>
        </Stack>
        {error && <div style={{ color: 'red' }}>{error}</div>}
        <Dialog open={showRequest} onClose={() => setShowRequest(false)} maxWidth="md" fullWidth>
          <DialogTitle>Request JSON</DialogTitle>
          <DialogContent>
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{requestJson}</pre>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowRequest(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Stack>
    );
  } else {
    // Optimization started
    modalContent = (
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button variant="outlined" size="small" onClick={handleShowRequest} disabled={loading}>
            Show Request
          </Button>
          <Button variant="outlined" size="small" onClick={handleDownloadRequest} disabled={loading}>
            Download Request
          </Button>
          <Chip label={meta.calendar_status || 'In Progress'} color={meta.calendar_status === 'done' ? 'success' : 'info'} />
          <Button variant="outlined" size="small" onClick={handlePollStatus} disabled={loading}>
            Poll Status
          </Button>
          {meta.calendar_result && (
            <>
              <Button variant="outlined" size="small" onClick={handleShowResult} disabled={loading}>
                Show Results
              </Button>
              <Button variant="outlined" size="small" onClick={handleDownloadResult} disabled={loading}>
                Download Results
              </Button>
            </>
          )}
        </Stack>
        {error && <div style={{ color: 'red' }}>{error}</div>}
        <Dialog open={showRequest} onClose={() => setShowRequest(false)} maxWidth="md" fullWidth>
          <DialogTitle>Request JSON</DialogTitle>
          <DialogContent>
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{requestJson}</pre>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowRequest(false)}>Close</Button>
          </DialogActions>
        </Dialog>
        <Dialog open={showResult} onClose={() => setShowResult(false)} maxWidth="md" fullWidth>
          <DialogTitle>Optimization Result</DialogTitle>
          <DialogContent>
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{resultJson}</pre>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowResult(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Stack>
    );
  }

  return (
    <>
      <IconButton onClick={() => setOpen(true)} size="small" title="Calendar Planning">
        <SettingsIcon />
      </IconButton>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Calendar Planning for: {plan.name}</DialogTitle>
        <DialogContent>
          {modalContent}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PlanOptimizationCell; 